import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
} from "./thunks";
import { PaymentMethod } from "@/types";
import { toast } from "react-toastify";

type ThunkErrorPayload = {
  message?: string;
  status?: number;
};

const buildFriendlyPaymentMethodError = (
  payload?: ThunkErrorPayload,
): string => {
  const raw =
    payload?.message || "Não foi possível salvar o método de pagamento.";
  const status = payload?.status;

  // Casos comuns de duplicidade: 409 (Conflict) ou 422 (validation)
  if (status === 409 || status === 422) {
    // Se o backend já envia um texto bom, mantemos, mas tentamos melhorar
    const lower = raw.toLowerCase();
    if (
      lower.includes("already") ||
      lower.includes("exists") ||
      lower.includes("já existe") ||
      lower.includes("duplic")
    ) {
      return "Já existe um método de pagamento com esse nome. Use outro nome ou edite o existente.";
    }
  }

  return raw;
};

interface PaginatedPaymentMethods {
  data: PaymentMethod[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaymentMethodsState {
  paymentMethods: PaginatedPaymentMethods;
  loading: boolean;
  error: string | null;
}

const initialState: PaymentMethodsState = {
  paymentMethods: { data: [] },
  loading: false,
  error: null,
};

const PaymentMethodSlice = createSlice({
  name: "paymentMethods",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.paymentMethods = { data: [] };
      state.loading = false;
      state.error = null;
    },
    clearPaymentMethodError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPaymentMethods.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.paymentMethods = { data: payload.data };
          } else {
            // Dados paginados
            state.paymentMethods = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        const payload = action.payload as ThunkErrorPayload | undefined;
        state.error =
          payload?.message || "Erro ao carregar métodos de pagamento";
        state.loading = false;
      })
      // Create
      .addCase(createPaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createPaymentMethod.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.paymentMethods.data.push(action.payload.data);
          toast.success("Método de pagamento cadastrado com sucesso!");
        },
      )
      .addCase(createPaymentMethod.rejected, (state, action) => {
        const payload = action.payload as ThunkErrorPayload | undefined;
        const friendly = buildFriendlyPaymentMethodError(payload);
        state.error = friendly;
        state.loading = false;
        toast.error(
          friendly ||
            "Não foi possível cadastrar o método de pagamento, tente novamente!",
        );
      })

      // Update
      .addCase(updatePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePaymentMethod.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.paymentMethods.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.paymentMethods.data[index] = action.payload.data;
          }
          toast.success("Método de pagamento atualizado com sucesso!");
        },
      )
      .addCase(updatePaymentMethod.rejected, (state, action) => {
        const payload = action.payload as ThunkErrorPayload | undefined;
        const friendly = buildFriendlyPaymentMethodError(payload);
        state.error = friendly;
        state.loading = false;
        toast.error(
          friendly || "Não foi possível atualizar o método de pagamento.",
        );
      })

      // Delete
      .addCase(deletePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deletePaymentMethod.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.paymentMethods.data = state.paymentMethods.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Método de pagamento excluído com sucesso!");
        },
      )
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        const payload = action.payload as ThunkErrorPayload | undefined;
        state.error =
          payload?.message || "Não foi possível excluir o método de pagamento";
        state.loading = false;
      });
  },
});

export const { clearSuppliers, clearPaymentMethodError } =
  PaymentMethodSlice.actions;
export default PaymentMethodSlice.reducer;
